/**
 * Shared marketplace helpers — wei formatting, status pill colours, debounce
 * hook. Lives next to the pages because none of these are useful elsewhere.
 */

import { useEffect, useState } from 'react';
import type { EscrowStatus, ListingStatus } from '@infinia/ida-sdk';

const WEI_PER_ADI = 10n ** 18n;

/** Format a uint256 wei string as a 4-decimal ADI string. */
export function weiToAdi(wei: string | bigint | undefined | null): string {
  if (wei === undefined || wei === null || wei === '') return '0';
  let n: bigint;
  try {
    n = typeof wei === 'bigint' ? wei : BigInt(wei);
  } catch {
    return '0';
  }
  const whole = n / WEI_PER_ADI;
  const frac = n % WEI_PER_ADI;
  if (frac === 0n) return whole.toString();
  // 4 decimals.
  const fracStr = frac.toString().padStart(18, '0').slice(0, 4).replace(/0+$/, '');
  return fracStr.length > 0 ? `${whole.toString()}.${fracStr}` : whole.toString();
}

/** Convert decimal ADI string into wei string (no scientific notation). */
export function adiToWei(adi: string): string {
  const trimmed = adi.trim();
  if (!trimmed || trimmed === '.') return '0';
  const [whole = '0', frac = ''] = trimmed.split('.');
  const cleanFrac = (frac + '0'.repeat(18)).slice(0, 18);
  const wholeBn = BigInt(whole.replace(/[^0-9]/g, '') || '0');
  const fracBn = BigInt(cleanFrac.replace(/[^0-9]/g, '') || '0');
  return (wholeBn * WEI_PER_ADI + fracBn).toString();
}

const listingStatusVariant: Record<ListingStatus, 'success' | 'warning' | 'neutral' | 'error'> = {
  active: 'success',
  paused: 'warning',
  sold_out: 'neutral',
  archived: 'error',
};

export function listingStatusBadge(status: ListingStatus | string): 'success' | 'warning' | 'neutral' | 'error' {
  return (listingStatusVariant as Record<string, 'success' | 'warning' | 'neutral' | 'error'>)[status] ?? 'neutral';
}

const escrowStatusVariant: Record<EscrowStatus, 'success' | 'warning' | 'info' | 'neutral' | 'error'> = {
  pending: 'warning',
  locked: 'info',
  claiming: 'info',
  claimed: 'info',
  confirming: 'info',
  confirmed: 'success',
  disputing: 'warning',
  disputed: 'error',
  resolving: 'warning',
  resolved_for_buyer: 'success',
  resolved_for_operator: 'success',
  resolved_split: 'success',
  timing_out: 'warning',
  timed_out: 'neutral',
  adjudicator_timing_out: 'warning',
};

export function escrowStatusBadge(status: EscrowStatus | string): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  return (escrowStatusVariant as Record<string, 'success' | 'warning' | 'info' | 'neutral' | 'error'>)[status] ?? 'neutral';
}

/** Buyer-actionable escrow states. */
export const BUYER_ACTIVE_ESCROW_STATES: ReadonlySet<string> = new Set([
  'pending',
  'locked',
  'claiming',
  'claimed',
  'disputing',
]);

export const BUYER_COMPLETED_ESCROW_STATES: ReadonlySet<string> = new Set([
  'confirming',
  'confirmed',
  'timing_out',
  'timed_out',
  'resolved_for_buyer',
  'resolved_for_operator',
  'resolved_split',
]);

export const BUYER_DISPUTED_ESCROW_STATES: ReadonlySet<string> = new Set([
  'disputed',
  'resolving',
  'adjudicator_timing_out',
]);

export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function shortAddr(s: string | undefined | null, len = 6): string {
  if (!s) return '';
  if (s.length <= len * 2 + 2) return s;
  return `${s.slice(0, len + 2)}…${s.slice(-len)}`;
}

export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/** Bucket an integer score into a marketplace bucket label. */
export function scoreBucket(score: number): 'positive' | 'neutral' | 'negative' {
  if (score >= 70) return 'positive';
  if (score >= 40) return 'neutral';
  return 'negative';
}
